import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';
import Typography from 'kingpin/atoms/Typography';
import usePopup from 'hooks/usePopup';
import { FILTER_PLATE_HEIGHT, FILTER_PLATE_MAX_WIDTH } from './Interactive';
import Icon from 'kingpin/atoms/Icon';
import { Link } from 'react-router-dom';
import SPECIFIC_SET_ROUTES from 'screens/DataManager/SpecificSet/routes';
import DetailsCard from 'screens/DataManager/DatasetFilters/SavedFilters/FilterSlideOut/DetailsCard';
import SavedFiltersContext from 'screens/DataManager/DatasetFilters/SavedFilters/context/SavedFiltersContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import Tooltip from 'components/Tooltip';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';

const OuterDiv = styled.div<{ isOpen: boolean }>`
  max-width: ${FILTER_PLATE_MAX_WIDTH}px;
  height: ${FILTER_PLATE_HEIGHT}px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  border-radius: 8px;
  padding: 0px 4px;
  background-color: ${(props) =>
    props.isOpen ? 'var(--color-bg-selected)' : 'var(--color-badge-info-bg)'};
`;

const CrossDiv = styled.div`
  display: flex;
  margin-right: 4px;

  &:hover {
    opacity: 0.5;
  }
`;

const SavedFilterPlate = ({
  savedFilter,
  onRemoved,
}: {
  savedFilter: SavedFilter;
  onRemoved: () => void;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { isOpen, open, close } = usePopup();

  return (
    <Tooltip isAltTooltip content={<DetailsCard filter={savedFilter} />}>
      <OuterDiv
        isOpen={isOpen}
        onMouseEnter={open}
        onMouseLeave={close}
        data-testid={`${savedFilter.name}-plate`}
      >
        <CrossDiv onClick={onRemoved}>
          <Icon icon={'cross'} />
        </CrossDiv>

        <Link
          to={SPECIFIC_SET_ROUTES.buildFilters({
            dataset: savedFilter.dataset,
            filterId: savedFilter.id,
            portal: selectedPortal,
          })}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <div style={{ display: 'flex', marginRight: '4px' }}>
            <Typography.Body type="Button Text" isEllipsis>
              {savedFilter.name}
            </Typography.Body>
          </div>

          <div style={{ display: 'flex' }}>
            <Icon icon="open-link" color={'var(--color-info-v2)'} />
          </div>
        </Link>
      </OuterDiv>
    </Tooltip>
  );
};

const Gate = ({
  savedFilterId,
  isScope,
}: {
  savedFilterId: string;
  isScope: boolean;
}) => {
  const { savedFilterIdLookup } = useContext(SavedFiltersContext);
  const { drillDowns, setDrillDowns, scope, setScope } =
    useContext(FilterPlatesContext);

  const savedFilter = savedFilterIdLookup[savedFilterId];

  const onRemoved = useCallback(() => {
    if (isScope) {
      setScope(scope.filter((d) => d.id !== savedFilterId));
    } else {
      setDrillDowns(drillDowns.filter((d) => d.id !== savedFilterId));
    }
  }, [savedFilterId, drillDowns, isScope, scope, setDrillDowns, setScope]);

  if (!savedFilter) {
    return null;
  }

  return <SavedFilterPlate savedFilter={savedFilter} onRemoved={onRemoved} />;
};

export default Gate;
